import { createFileRoute, createLink } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog/Dialog";
import { ErrorPanel } from "@/components/Errors/ErrorPanel";
import { IconSadFace } from "@/components/Icons";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { SearchDataMissingError } from "@/errors/SearchDataMissingError";
import { HotelRoomSelection } from "@/features/hotels/components/RoomSelection/HotelRoomSelection";
import { useHotelBookingContext } from "@/features/hotels/contexts/HotelBookingContext";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelOffersContext } from "@/features/hotels/contexts/HotelOffersContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { useHotelRoomAvailabilityMutation } from "@/features/hotels/hooks/useHotelRoomAvailability";
import { useHotelSearch } from "@/features/hotels/hooks/useHotelSearch";
import { calculateBookingPrice } from "@/features/hotels/utils/calculateBookingPrice";
import { getNumberOfBookingNights } from "@/features/hotels/utils/getNumberOfBookingNights";
import { HotelTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type { GetHotelAvailabilityTokenOutputData } from "@/types/codegen";
import { isOfferIsNotAvailableAnymoreError } from "@/utils/assertions";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/rooms",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

const ButtonLink = createLink(Button);

function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();

  const { data: offers } = useHotelOffersContext();
  const { data: hotelData } = useHotelDataContext();
  const { searchResultKey, searchResultToken } = useHotelSearch();
  const { setData: setHotelBooking, setAvailabilityToken } =
    useHotelBookingContext();
  const { searchData } = useHotelSearchDataContext();

  if (!searchResultKey || !searchResultToken || !searchData || !hotelData) {
    throw new SearchDataMissingError("Hotel search data is missing");
  }

  const {
    mutateAsync: getRoomAvailability,
    isPending: isRoomAvailabilityPending,
    error: roomAvailabilityError,
  } = useHotelRoomAvailabilityMutation({
    onSuccess: (data: GetHotelAvailabilityTokenOutputData) => {
      setAvailabilityToken(data.availabilityToken);
    },
  });

  const bookingNights = getNumberOfBookingNights({
    checkIn: searchData.dates.checkIn,
    checkOut: searchData.dates.checkOut,
  });

  const guestCount = searchData.rooms.reduce(
    (total, room) => total + room.adults + room.children,
    0,
  );

  return (
    <>
      <div className="w-full px-main">
        <div className="mb-4 text-xl font-medium">
          {intl.formatMessage({
            defaultMessage: "Available room options",
            description:
              "Section heading above the list of available room types",
          })}
        </div>

        <div className="space-y-3">
          {offers.map((offer) => (
            <HotelRoomSelection
              key={offer.packageToken}
              hotel={hotelData}
              rooms={offer.rooms}
              guestCount={guestCount}
              bookingNights={bookingNights}
              onRoomSelected={async (selectedRooms) => {
                const bookingData = {
                  packageToken: offer.packageToken,
                  rooms: selectedRooms,
                  offerId: offer.offerId,
                };
                setHotelBooking(bookingData);

                const totalPrice = calculateBookingPrice(bookingData);

                await getRoomAvailability({
                  hotelId: hotelData.id.toString(),
                  searchResultKey,
                  searchResultToken,
                  packageToken: bookingData.packageToken,
                  offerId: bookingData.offerId,
                  roomTokens: bookingData.rooms.map((room) => room.roomToken),
                });

                rudderAnalytics.track(
                  HotelTrackingEvents.USER_PROCEED_AFTER_ROOM_SELECTION,
                  {
                    bookingType: "Hotel",
                    selected_offer_id: bookingData.offerId,
                    price: totalPrice.value,
                  },
                );
                navigate({ to: "/hotels/$id/travellers" });
              }}
              onRoomGalleryClick={(option, index) => {
                navigate({
                  to: "./$roomToken/images/fullscreen",
                  params: { roomToken: option.roomToken },
                  search: { index },
                });
              }}
            />
          ))}
        </div>
      </div>

      <Dialog
        isOpen={
          isRoomAvailabilityPending ||
          isOfferIsNotAvailableAnymoreError(roomAvailabilityError)
        }
      >
        {isRoomAvailabilityPending ? (
          <PageLoader>
            <AnimatedLoader icon="buildings" />
            <h3 className="mt-3 font-normal text-text-primary">
              {intl.formatMessage({
                defaultMessage: "Checking room availability...",
                description:
                  "Loading message shown while room availability is being checked",
              })}
            </h3>
          </PageLoader>
        ) : (
          <ErrorPanel
            icon={IconSadFace}
            primaryText={intl.formatMessage({
              defaultMessage: "This hotel offer is not available anymore",
              description:
                "Error heading when the selected hotel offer has expired",
            })}
            secondaryText={intl.formatMessage({
              defaultMessage:
                "Unfortunately, this hotel offer is not available anymore. Please try again later or choose another hotel.",
              description:
                "Explanation that the selected hotel offer is no longer available",
            })}
          >
            <ButtonLink
              variant="outlined"
              borderRoundness="medium"
              color="primary"
              size="small"
              to="/hotels/list"
            >
              {intl.formatMessage({
                defaultMessage: "Back to search",
                description:
                  "Button to return to search when the selected offer has expired or is unavailable",
              })}
            </ButtonLink>
          </ErrorPanel>
        )}
      </Dialog>
    </>
  );
}
